"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

/*
 * One emoji, as the Unicode emoji specification defines it, matched in a single pass so
 * every alternative counts once:
 *
 *  1. a keycap: a digit, # or *, an optional U+FE0F and the combining keycap U+20E3
 *  2. a flag: a pair of regional indicators
 *  3. a pictographic sequence. It starts at a character that is an emoji by default
 *     (Emoji_Presentation) or at a text-style symbol that has been asked to look like one
 *     with U+FE0F, or that is the first half of a joined emoji. A bare ♀ ♂ ↔ ★ ♥ © ® ™
 *     is therefore text and stays. Skin tones and tag characters (the England flag) ride
 *     along, and a zero-width joiner (U+200D) is consumed only when it links two emoji,
 *     so a joiner in Indic or Persian text is never touched.
 */
const EMOJI_RE =
  /[0-9#*]\uFE0F?\u20E3|[\u{1F1E6}-\u{1F1FF}]{2}|(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}(?:\uFE0F|(?=\u200D\p{Extended_Pictographic})))[\uFE0E\uFE0F]?[\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}]*(?:\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})[\uFE0E\uFE0F]?[\u{1F3FB}-\u{1F3FF}\u{E0020}-\u{E007F}]*)*/gu;
// Invisible emoji plumbing left without its emoji: a keycap combiner or a tag character.
const LEFTOVER_MODIFIER_RE = /[\u20E3\u{E0020}-\u{E007F}]/gu;
// Runs of two or more spaces/tabs (but not newlines).
const MULTI_SPACE_RE = /[^\S\n]{2,}/g;

interface RemovalResult {
  text: string;
  count: number;
}

export function removeEmoji(input: string): RemovalResult {
  const count = input.match(EMOJI_RE)?.length ?? 0;
  const text = input.replace(EMOJI_RE, "").replace(LEFTOVER_MODIFIER_RE, "");
  return { text, count };
}

export function collapseSpaces(text: string): string {
  return text.replace(MULTI_SPACE_RE, " ");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const EMOJI_REMOVER_STRINGS = {
  inputText: "Input text",
  inputPlaceholder: "Paste text containing emoji here",
  collapseDoubleSpaces: "Collapse the double spaces left behind",
  removeEmoji: "Remove Emoji",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  cleanedOutput: "Cleaned output",
  emojiRemoved: "Emoji removed",
  inputCharacters: "Input characters",
  outputCharacters: "Output characters",
} as const;

export default function EmojiRemoverWidget() {
  const [input, setInput] = useState("");
  const [collapse, setCollapse] = useState(true);
  const [output, setOutput] = useState("");
  const [removedCount, setRemovedCount] = useState(0);
  const [hasResult, setHasResult] = useState(false);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(EMOJI_REMOVER_STRINGS);

  const fmt = useToolNumber();

  function handleRemove() {
    if (input.length === 0) {
      return;
    }
    const result = removeEmoji(input);
    const cleaned = collapse ? collapseSpaces(result.text) : result.text;
    setOutput(cleaned);
    setRemovedCount(result.count);
    setHasResult(true);
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setRemovedCount(0);
    setHasResult(false);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="emoji-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="emoji-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Options */}
      <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
        <input
          checked={collapse}
          className="accent-primary"
          onChange={(e) => setCollapse(e.target.checked)}
          type="checkbox"
        />
        {strings.collapseDoubleSpaces}
      </label>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleRemove}
          type="button"
        >
          {strings.removeEmoji}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {hasResult && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="emoji-output"
          >
            {strings.cleanedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="emoji-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {hasResult && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.emojiRemoved} value={fmt(removedCount)} />
          <StatCard label={strings.inputCharacters} value={fmt(input.length)} />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(output.length)}
          />
        </div>
      )}
    </>
  );
}
